import { useCallback, useEffect, useRef, useState } from "react";
import { BookOpenCheck, FileDown, Loader2, Plus, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, apiError } from "@/lib/api";
import { TIPO_EVENTO_POLIZA, TIPOS_POLIZA_MANUAL, fmtMoney } from "@/lib/fideicomisos";
import { toast } from "sonner";

const hoyMes = () => new Date().toISOString().slice(0, 7);

export function ContabilidadTab({ fid, puedeEscribir }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const [periodo, setPeriodo] = useState(hoyMes());
  const [abrir, setAbrir] = useState(false);
  const [tipo, setTipo] = useState("gasto_conservacion");
  const [concepto, setConcepto] = useState("");
  const [motivo, setMotivo] = useState("");
  const [polizaRef, setPolizaRef] = useState("");
  const [docRef, setDocRef] = useState({ id: "", nombre: "" });
  const [movs, setMovs] = useState([{ cuenta: "5101", cargo: "", abono: "" }, { cuenta: "1102", cargo: "", abono: "" }]);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setBusy("load");
    try {
      const { data: d } = await api.get(`/fideicomisos/${fid.id}/contabilidad`);
      setData(d);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(""); }
  }, [fid.id]);
  useEffect(() => { load(); }, [load]);

  const subirDocRef = async (file) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("tipo_documento", "Soporte de póliza fiduciaria");
      fd.append("clasificacion", "Fideicomisos");
      fd.append("categoria", "fideicomiso_contabilidad");
      fd.append("referencia_id", fid.id);
      const { data: d } = await api.post("/documentos/upload", fd);
      setDocRef({ id: d.id, nombre: d.nombre_archivo });
      toast.success(`Soporte cargado: ${d.nombre_archivo}`);
    } catch (e) { toast.error(apiError(e)); }
  };

  const crearPoliza = async () => {
    setBusy("crear");
    try {
      const body = {
        tipo_evento: tipo, concepto, motivo,
        referencia: tipo === "correccion" ? { poliza_id: polizaRef } : { doc_id: docRef.id },
        movimientos: movs.map((m) => ({ cuenta: m.cuenta, concepto, cargo: m.cargo || 0, abono: m.abono || 0 })),
      };
      await api.post(`/fideicomisos/${fid.id}/polizas`, body);
      toast.success("Póliza registrada (append-only).");
      setAbrir(false); setConcepto(""); setMotivo(""); setPolizaRef(""); setDocRef({ id: "", nombre: "" });
      setMovs([{ cuenta: "5101", cargo: "", abono: "" }, { cuenta: "1102", cargo: "", abono: "" }]);
      await load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(""); }
  };

  const descargarEstado = async () => {
    setBusy("pdf");
    try {
      const res = await api.get(`/fideicomisos/${fid.id}/estado-cuenta`, { params: { periodo }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `EstadoCuenta_${fid.folio}_${periodo}.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success(`Estado de cuenta ${periodo} emitido y ligado al expediente (Art. 391).`);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(""); }
  };

  if (!data) return <div className="p-6 text-sm text-slate-500">Cargando libro fiduciario…</div>;
  const cuentas = data.cuentas || {};

  return (
    <div className="space-y-4" data-testid="contabilidad-tab">
      {/* Mayor */}
      <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <p className="flex items-center gap-2 text-sm font-semibold text-slate-800">
            <BookOpenCheck className="h-4 w-4" /> Mayor fiduciario (Art. 386 — libro separado)
          </p>
          <div className="flex items-center gap-2">
            {data.en_ceros
              ? <span data-testid="conta-mayor-en-ceros" className="rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-semibold text-emerald-700">Mayor en ceros ✓</span>
              : <span data-testid="conta-mayor-con-saldos" className="rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-700">Con saldos abiertos</span>}
            <Button data-testid="conta-sincronizar-btn" size="sm" variant="outline" disabled={busy === "load"} onClick={load}>
              {busy === "load" ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="mr-1 h-3.5 w-3.5" />}Sincronizar
            </Button>
          </div>
        </div>
        <table className="w-full text-xs">
          <thead><tr className="border-b border-slate-200 text-left text-slate-500">
            <th className="py-1.5">Cuenta</th><th>Nombre</th><th className="text-right">Cargos</th><th className="text-right">Abonos</th><th className="text-right">Saldo</th>
          </tr></thead>
          <tbody>
            {(data.mayor || []).map((f) => (
              <tr key={f.cuenta} className="border-b border-slate-50" data-testid={`conta-mayor-${f.cuenta}`}>
                <td className="py-1.5 font-mono">{f.cuenta}</td><td>{f.nombre}</td>
                <td className="text-right">{fmtMoney(f.cargos)}</td><td className="text-right">{fmtMoney(f.abonos)}</td>
                <td className={`text-right font-semibold ${Math.abs(f.saldo) > 0.01 ? "text-slate-900" : "text-slate-400"}`}>{fmtMoney(f.saldo)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="mt-3 text-[11px] text-slate-400">{data.nota_honorarios}</p>
      </div>

      {/* Estado de cuenta Art. 391 */}
      <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
        <p className="mb-3 text-sm font-semibold text-slate-800">Rendición de cuentas (Art. 391 LGTOC)</p>
        <div className="flex flex-wrap items-center gap-3">
          <Input data-testid="conta-periodo-input" type="month" value={periodo} onChange={(e) => setPeriodo(e.target.value)} className="w-44" />
          {puedeEscribir && (
            <Button data-testid="conta-estado-cuenta-btn" size="sm" disabled={busy === "pdf"} onClick={descargarEstado}>
              {busy === "pdf" ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <FileDown className="mr-1 h-4 w-4" />}Emitir estado de cuenta PDF
            </Button>
          )}
          <p className="text-[11px] text-slate-400">Se emite con sello SHA-256 y queda ligado al expediente del fideicomiso.</p>
        </div>
      </div>

      {/* Pólizas */}
      <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
        <div className="mb-3 flex items-center justify-between">
          <p className="text-sm font-semibold text-slate-800">Pólizas del libro fiduciario ({(data.polizas || []).length}) — append-only</p>
          {puedeEscribir && (
            <Button data-testid="conta-nueva-poliza-btn" size="sm" variant="outline" onClick={() => setAbrir(true)}>
              <Plus className="mr-1 h-3.5 w-3.5" />Póliza manual
            </Button>
          )}
        </div>
        <div className="max-h-96 space-y-2 overflow-y-auto">
          {(data.polizas || []).map((p) => (
            <div key={p.id} className="rounded-md border border-slate-100 p-3 text-xs" data-testid={`conta-poliza-${p.folio_poliza}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="font-semibold text-slate-800">{p.folio_poliza} · {p.fecha} · {TIPO_EVENTO_POLIZA[p.tipo_evento] || p.tipo_evento}
                  <span className={`ml-2 rounded-full px-2 py-0.5 text-[10px] ${p.origen === "automatica" ? "bg-blue-50 text-blue-600" : "bg-slate-100 text-slate-600"}`}>{p.origen}</span>
                </span>
                <span className="font-semibold">{fmtMoney(p.total_cargos)}</span>
              </div>
              <p className="mt-0.5 text-slate-500">{p.concepto}</p>
              <div className="mt-1.5 grid grid-cols-1 gap-0.5 md:grid-cols-2">
                {(p.movimientos || []).map((m, i) => (
                  <span key={i} className="font-mono text-[10px] text-slate-500">
                    {m.cuenta} {cuentas[m.cuenta]?.slice(0, 24)} — {m.cargo > 0 ? `cargo ${fmtMoney(m.cargo)}` : `abono ${fmtMoney(m.abono)}`}
                  </span>
                ))}
              </div>
            </div>
          ))}
          {(data.polizas || []).length === 0 && <p className="text-xs text-slate-400">Sin pólizas registradas todavía.</p>}
        </div>
      </div>

      {/* Dialog póliza manual */}
      <Dialog open={abrir} onOpenChange={setAbrir}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Póliza manual (partida doble, append-only)</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Select value={tipo} onValueChange={setTipo}>
                <SelectTrigger data-testid="conta-tipo-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TIPOS_POLIZA_MANUAL.map((t) => <SelectItem key={t} value={t}>{TIPO_EVENTO_POLIZA[t]}</SelectItem>)}
                </SelectContent>
              </Select>
              {tipo === "correccion"
                ? <Input data-testid="conta-poliza-ref-input" value={polizaRef} onChange={(e) => setPolizaRef(e.target.value)} placeholder="ID de la póliza a corregir" />
                : (
                  <div>
                    <input ref={fileRef} type="file" accept="application/pdf" className="hidden" onChange={(e) => subirDocRef(e.target.files?.[0])} data-testid="conta-doc-file-input" />
                    <Button data-testid="conta-doc-upload-btn" type="button" size="sm" variant="outline" className="w-full" onClick={() => fileRef.current?.click()}>
                      {docRef.nombre ? `PDF: ${docRef.nombre.slice(0, 26)}…` : "Documento soporte (PDF, obligatorio)"}
                    </Button>
                  </div>
                )}
            </div>
            <Input data-testid="conta-concepto-input" value={concepto} onChange={(e) => setConcepto(e.target.value)} placeholder="Concepto" />
            {tipo === "correccion" && (
              <Textarea data-testid="conta-motivo-input" value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Motivo de la corrección (obligatorio)" rows={2} />
            )}
            <div className="space-y-2">
              {movs.map((m, i) => (
                <div key={i} className="grid grid-cols-3 gap-2">
                  <Select value={m.cuenta} onValueChange={(v) => setMovs((s) => s.map((x, j) => (j === i ? { ...x, cuenta: v } : x)))}>
                    <SelectTrigger data-testid={`conta-mov-cuenta-${i}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(cuentas).map(([c, n]) => <SelectItem key={c} value={c}>{c} — {n}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Input data-testid={`conta-mov-cargo-${i}`} type="number" min="0" value={m.cargo} placeholder="Cargo"
                         onChange={(e) => setMovs((s) => s.map((x, j) => (j === i ? { ...x, cargo: e.target.value } : x)))} />
                  <Input data-testid={`conta-mov-abono-${i}`} type="number" min="0" value={m.abono} placeholder="Abono"
                         onChange={(e) => setMovs((s) => s.map((x, j) => (j === i ? { ...x, abono: e.target.value } : x)))} />
                </div>
              ))}
              <Button size="sm" variant="ghost" data-testid="conta-agregar-mov-btn" onClick={() => setMovs((s) => [...s, { cuenta: "1102", cargo: "", abono: "" }])}>+ Movimiento</Button>
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="conta-guardar-poliza-btn" disabled={!concepto || busy === "crear"} onClick={crearPoliza}>
              {busy === "crear" ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}Registrar póliza
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
